import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, AlertCircle, CreditCard, FileCheck, Calendar, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { BankAccount, PensionConsent } from '@/types/pensions';
import { BankAccountForm } from './BankAccountForm';
import { PensionSetupConsent } from './PensionSetupConsent';
import PensionAPIService from '@/services/pension-api';

interface PensionSetupInfoProps {
  bankAccount?: BankAccount | null;
  consent?: PensionConsent | null;
  isLoading?: boolean;
  onBankAccountUpdate?: (bankAccount: BankAccount) => void;
  onConsentUpdate?: (consent: PensionConsent) => void;
  showPostponementMode?: boolean; // New prop to show postponement-specific messaging
}

export default function PensionSetupInfo({ 
  bankAccount, 
  consent, 
  isLoading = false,
  onBankAccountUpdate,
  onConsentUpdate,
  showPostponementMode = false
}: PensionSetupInfoProps) {
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [isSavingBankAccount, setIsSavingBankAccount] = useState(false);
  const [isSavingConsent, setIsSavingConsent] = useState(false);
  
  const formatSortCode = (sortCode: string) => {
    if (!sortCode || sortCode.length !== 6) return sortCode;
    return `${sortCode.slice(0, 2)}-${sortCode.slice(2, 4)}-${sortCode.slice(4, 6)}`;
  };

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber || accountNumber.length < 4) return accountNumber;
    return `****${accountNumber.slice(-4)}`;
  };

  const handleBankAccountSubmit = async (data: BankAccount) => {
    setIsSavingBankAccount(true);
    try {
      const savedBankAccount = await PensionAPIService.saveBankAccount(data);
      if (onBankAccountUpdate) {
        onBankAccountUpdate(savedBankAccount);
      }
      setBankAccountDialogOpen(false);
    } catch (error) {
      console.error('Failed to save bank account:', error);
    } finally {
      setIsSavingBankAccount(false);
    }
  };

  const handleConsentSubmit = async (consentData: PensionConsent) => {
    setIsSavingConsent(true);
    try {
      const savedConsent = await PensionAPIService.recordConsent(consentData);
      if (onConsentUpdate) {
        onConsentUpdate(savedConsent);
      }
      setConsentDialogOpen(false);
    } catch (error) {
      console.error('Failed to save consent:', error);
    } finally {
      setIsSavingConsent(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg">Pension Setup Information</CardTitle>
        {showPostponementMode && (
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Postponement Mode:</strong> You have active postponements. During postponement, only consent is required. 
              Bank account details can be provided later when you're ready to start contributions.
            </p>
          </div>
        )}
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        {/* Bank Account Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-gray-600" />
              <h3 className="font-medium">Employer Bank Account</h3>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setBankAccountDialogOpen(true)}
              className="text-xs"
            >
              <Edit className="h-3 w-3 mr-1" />
              {bankAccount ? 'Edit' : 'Set Up'}
            </Button>
          </div>
          
          {bankAccount ? (
            <div className="rounded-lg border bg-gray-50 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Configured
                </Badge>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Bank Name:</span>
                  <span className="font-medium">{bankAccount.bankName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sort Code:</span>
                  <span className="font-medium">{formatSortCode(bankAccount.sortCode)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Account Number:</span>
                  <span className="font-medium">{maskAccountNumber(bankAccount.accountNumber)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className={`rounded-lg border p-4 ${
              showPostponementMode 
                ? "border-blue-200 bg-blue-50" 
                : "border-yellow-200 bg-yellow-50"
            }`}>
              <div className="flex items-start space-x-2">
                <AlertCircle className={`h-4 w-4 mt-0.5 ${
                  showPostponementMode ? "text-blue-600" : "text-yellow-600"
                }`} />
                <div className="space-y-1">
                  <p className={`text-sm font-medium ${
                    showPostponementMode ? "text-blue-800" : "text-yellow-800"
                  }`}>
                    {showPostponementMode ? "Bank Account (Optional for Postponement)" : "Bank Account Required"}
                  </p>
                  <p className={`text-xs ${
                    showPostponementMode ? "text-blue-700" : "text-yellow-700"
                  }`}>
                    {showPostponementMode 
                      ? "Bank account can be set up later when you're ready to start pension contributions."
                      : "Set up your bank account to enable pension contributions."
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Consent Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileCheck className="h-5 w-5 text-gray-600" />
              <h3 className="font-medium">Consent & Terms</h3>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConsentDialogOpen(true)}
              className="text-xs"
            >
              <Edit className="h-3 w-3 mr-1" />
              {consent ? 'Review' : 'Set Up'}
            </Button>
          </div>
          
          {consent ? (
            <div className="rounded-lg border bg-gray-50 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Accepted
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <p className="text-xs text-gray-700">Hirelo contact authorization granted</p>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <p className="text-xs text-gray-700">NEST terms & conditions accepted</p>
                </div>
                {consent.consentDate && (
                  <div className="flex items-center space-x-2 pt-2 border-t">
                    <Calendar className="h-3 w-3 text-gray-500" />
                    <p className="text-xs text-gray-600">
                      Consent given on {format(new Date(consent.consentDate), 'dd/MM/yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-yellow-800">Consent Required</p>
                  <p className="text-xs text-yellow-700">
                    Provide consent for Hirelo to manage pensions on your behalf.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Bank Account Dialog */}
    <Dialog open={bankAccountDialogOpen} onOpenChange={setBankAccountDialogOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {bankAccount ? 'Edit Bank Account' : 'Set Up Bank Account'}
          </DialogTitle>
        </DialogHeader>
        <BankAccountForm
          initialData={bankAccount || undefined}
          onSubmit={handleBankAccountSubmit}
          isLoading={isSavingBankAccount}
        />
      </DialogContent>
    </Dialog>

    {/* Consent Dialog */}
    <Dialog open={consentDialogOpen} onOpenChange={setConsentDialogOpen}>
      <DialogContent className="sm:max-w-[600px] md:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>
            {consent ? 'Review Consent & Terms' : 'Pension Setup Consent'}
          </DialogTitle>
        </DialogHeader>
        <PensionSetupConsent
          initialConsent={consent || undefined}
          termsUrl={PensionAPIService.getPensionTermsPdfUrl()}
          onConsentSubmit={handleConsentSubmit}
          isLoading={isSavingConsent}
        />
      </DialogContent>
    </Dialog>
    </>
  );
}